// Option lists for the report widget edit form (geo, sort, preset, lens,
// affinity, life-cycle, TikTok cluster + health-stat filters). Colocated with
// the Reports edit UI that consumes them.
//
// CONTRACT DUPLICATION (known debt): the key lists below mirror the
// get_entity_data enums in ai-services/assistant/tools.py (GET_ENTITY_DATA_SCHEMA)
// and backend/api/api2/services/ai/entity_data_tiktok.py (TIKTOK_HASHTAG_SORTS /
// TIKTOK_CREATOR_SORTS / TIKTOK_POST_SORTS / TIKTOK_HASHTAG_PRESETS). Labels are
// DERIVED from the existing metric metadata + copy rather than hand-typed, so a
// label change there flows through here. Keep the key lists in sync when the
// backend adds a sort/preset/filter.

import {
    POPULARITY_METADATA_BY_KEY,
    TIKTOK_METADATA_BY_KEY,
} from '@/utils/metrics';
import { lifeCycleFilterOptions } from '@/utils/filters';
import { tooltips } from '@/copy';

// --- order_by --------------------------------------------------------------

// A metric's human label comes straight from its metadata (the dashboard's own
// column label); fall back to the raw key if a metric isn't in the map.
const sortLabel = (map, key) =>
    map[key]?.getSortOption?.({ ascending: false })?.label ?? key;

const sortOptions = (map, keys) =>
    keys.map((value) => ({ value, label: sortLabel(map, value) }));

const POP_SORT_KEYS = [
    'popularity_index_avg_12_month',
    'YoY',
    'increase_YoY',
    'QoQ',
    'increase_QoQ',
    'MoM',
    'increase_MoM',
];

const TIKTOK_HASHTAG_SORT_KEYS = [
    'non_cum_avg_weekly_views',
    'views',
    'non_cum_increase_WoW',
    'non_cum_WoW',
    'non_cum_increase_MoM_rolling',
    'non_cum_MoM_rolling',
    'non_cum_increase_QoQ_rolling',
    'non_cum_QoQ_rolling',
    'non_cum_increase_YoY_rolling',
    'non_cum_YoY_rolling',
    'total_posts_cnt',
    'partner_paid_partnership_posts_views_prc',
    'eligible_for_commission_posts_views_prc',
    'engagement_prc',
];

const TIKTOK_CREATOR_SORT_KEYS = [
    'nb_likes_last_week',
    'nb_likes_all',
    'nb_views_last_week',
    'nb_views_all',
    'engagement_prc_all',
    'paid_posts_views_prc_all',
    'eligible_for_commission_posts_views_prc_all',
];

// avg_weekly_views ("last month") leads: it's the backend default. post_rank is the
// store's internal ordering (not user-facing), so it's omitted.
const TIKTOK_POST_SORT_KEYS = [
    'avg_weekly_views',
    'total_post_views',
    'last_week_views',
    'non_cum_WoW',
    'sentiment_score',
    'create_timestamp',
];

// Related posts accept the same sort set and the same default as plain posts
// (backend TIKTOK_RELATED_POST_SORTS = TIKTOK_POST_SORTS, DEFAULT_RELATED_POST_SORT
// = DEFAULT_POST_SORT), so the widget offers the same keys — otherwise a related
// widget harvested with e.g. non_cum_WoW couldn't re-select its active sort here.
const TIKTOK_RELATED_POST_SORT_KEYS = TIKTOK_POST_SORT_KEYS;

// Keyed `${data_source}:${entity_relationship}` — only relationships that take a
// sort appear (the `self` overview cards have none; `google:related_terms` is
// deliberately absent — its store keeps a single hardcoded volume ranking).
export const ORDER_BY_BY_REL = {
    'pop_index:brands': sortOptions(POPULARITY_METADATA_BY_KEY, POP_SORT_KEYS),
    'pop_index:cat5s': sortOptions(POPULARITY_METADATA_BY_KEY, POP_SORT_KEYS),
    'pop_index:related_categories': sortOptions(
        POPULARITY_METADATA_BY_KEY,
        POP_SORT_KEYS,
    ),
    'pop_index:categories': sortOptions(
        POPULARITY_METADATA_BY_KEY,
        POP_SORT_KEYS,
    ),
    'tiktok:hashtags': sortOptions(
        TIKTOK_METADATA_BY_KEY,
        TIKTOK_HASHTAG_SORT_KEYS,
    ),
    'tiktok:creators': sortOptions(
        TIKTOK_METADATA_BY_KEY,
        TIKTOK_CREATOR_SORT_KEYS,
    ),
    'tiktok:posts': sortOptions(TIKTOK_METADATA_BY_KEY, TIKTOK_POST_SORT_KEYS),
    'tiktok:related_posts': sortOptions(
        TIKTOK_METADATA_BY_KEY,
        TIKTOK_RELATED_POST_SORT_KEYS,
    ),
};

export const orderByOptionsFor = (dataSource, entityRelationship) =>
    ORDER_BY_BY_REL[`${dataSource}:${entityRelationship}`] ?? [];

// --- TikTok hashtag preset -------------------------------------------------

// Wire values are snake_case (TIKTOK_HASHTAG_PRESETS keys), NOT the camelCase
// values in the legacy TikTokPresetFilterChips.jsx.
export const PRESET_OPTIONS = [
    { value: 'trending_this_week', label: 'Trending This Week' },
    { value: 'trending_this_month', label: 'Trending This Month' },
    { value: 'organic_trends', label: 'Organic Trends' },
    { value: 'high_engagement', label: 'High Engagement' },
    { value: 'new', label: 'New' },
];

// --- related-categories affinity group -------------------------------------

export const AFFINITY_GROUP_OPTIONS = [
    'ingredients',
    'product format',
    'benefits',
    'concerns',
    'retailers',
    'demographics',
    'colors & prints',
    'questions',
].map((value) => ({ value, label: value }));

// --- self-card analysis lens -----------------------------------------------

export const ANALYSIS_LENS_OPTIONS = [
    { value: 'overview', label: 'Overview' },
    { value: 'short_term', label: 'Short term' },
    { value: 'long_term', label: 'Long term' },
    { value: 'social', label: 'Social' },
];

// --- life cycle (reused from the dashboard filter options) -----------------

export const LIFE_CYCLE_OPTIONS = lifeCycleFilterOptions.map(
    ({ value, label }) => ({ value, label }),
);

// --- price point ------------------------------------------------------------

const PRICE_WIRE_BY_SCORE = {
    10: 'HIGH PRICE',
    5: 'MEDIUM PRICE',
    0: 'LOW PRICE',
};

export const PRICE_OPTIONS = Object.entries(
    tooltips?.healthStats?.['price point']?.scoreLabels ?? {},
)
    .map(([score, label]) => ({ value: PRICE_WIRE_BY_SCORE[score], label }))
    .filter((option) => option.value);

// --- TikTok hashtag granular filters (cluster + health-stat buckets) -------

// Un-prefixed cluster values, matching the API (the dashboard-URL builder
// re-adds the "tiktok " prefix). See entity_data_tiktok.py.
export const CLUSTER_OPTIONS = [
    'rising star',
    'sustained riser',
    'seasonal riser',
    'no cluster',
].map((value) => ({ value, label: value }));

// Each health-stat dimension is an array of score buckets (0/2/4/6/8). Bucket
// labels are pulled from the shared health-stat copy so they match the dashboard
// tooltips. metricLabel keys mirror entity_data_tiktok._HS_KEY_TO_METRIC_LABEL.
const HS_DIMENSIONS = [
    {
        key: 'hs_average_weekly_views',
        label: 'Average weekly views',
        metricLabel: 'average weekly views',
    },
    { key: 'hs_paid_views', label: 'Paid views', metricLabel: 'paid views' },
    { key: 'hs_posts', label: 'Posts', metricLabel: 'posts' },
    { key: 'hs_tiktok_shop', label: 'TikTok shop', metricLabel: 'TikTok shop' },
    { key: 'hs_engagement', label: 'Engagement', metricLabel: 'engagement' },
];

const bucketOptions = (metricLabel) => {
    const stat = tooltips?.healthStats?.[metricLabel];
    const labels = stat?.scoreLabelsShort ?? stat?.scoreLabels ?? {};
    return Object.keys(labels)
        .map(Number)
        .sort((a, b) => b - a) // 8 → 0 (best first)
        .map((score) => ({ value: score, label: labels[String(score)] }));
};

export const HEALTH_STAT_FILTERS = HS_DIMENSIONS.map((dimension) => ({
    ...dimension,
    buckets: bucketOptions(dimension.metricLabel),
}));

// --- geo -------------------------------------------------------------------

// Map a country list (from useFetchCountries / useFetchTikTokCountries) to
// `{ value: text_id, label }` options; `geo` is wired as the country text_id.
export const geoOptions = (countries = []) =>
    countries
        .filter((c) => c?.text_id)
        .map((c) => ({ value: c.text_id, label: c.name ?? c.text_id }));
